import {Component} from 'panel';

import {copyInputElementText, onAnimationEnd, offAnimationEnd} from 'common/util/dom';
import {registerMPElement} from 'common/util/register-element';

import template from './index.jade';
import css from './index.styl?inline';

const states = Object.freeze({
  initial: `INITIAL`, // Share button is clickable
  clicked: `CLICKED`, // Share button has been clicked, animate out
  complete: `COMPLETE`, // Animate resolution
  reset: `RESET`, // Animate the share button back in
});

const nextStates = Object.freeze({
  [states.clicked]: states.complete,
  [states.complete]: states.reset,
  [states.reset]: states.initial,
});

export default class ShareButton extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'data-text': `string`,
    };
  }

  get states() {
    return states;
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        status: states.initial,
      },
      helpers: {
        handleClick: (event) => {
          event.preventDefault();
          event.stopPropagation();

          const dummy = this.createDummy(this.attr(`data-text`));
          document.body.appendChild(dummy);
          const copied = copyInputElementText(dummy);
          document.body.removeChild(dummy);

          this.dispatchEvent(new CustomEvent(`share`, {bubbles: true}));

          if (copied) {
            this.update({
              status: states.clicked,
            });
          } else {
            // TODO (dwoodall): Implement fallback for older browsers
          }
        },
      },
    };
  }

  advanceToNextState() {
    this.update({
      status: nextStates[this.state.status],
    });
  }

  connectedCallback() {
    super.connectedCallback(...arguments);
    if (!this._advanceToNextState) {
      this._advanceToNextState = () => this.advanceToNextState();
      onAnimationEnd(this.el, this._advanceToNextState);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback(...arguments);
    if (this._advanceToNextState) {
      offAnimationEnd(this.el, this._advanceToNextState);
    }
  }

  createDummy(text) {
    const isRTL = document.documentElement.getAttribute(`dir`) === `rtl`;

    const dummy = document.createElement(`textarea`);

    dummy.style.fontSize = `12pt`; // Prevent zooming on iOS

    // Reset box model
    dummy.style.border = `0`;
    dummy.style.padding = `0`;
    dummy.style.margin = `0`;

    // Move element out of screen horizontally
    dummy.style.position = `absolute`;
    dummy.style[isRTL ? `right` : `left`] = `-9999px`;

    // Move element to the same position vertically
    const yPosition = window.pageYOffset || document.documentElement.scrollTop;
    dummy.style.top = `${yPosition}px`;

    dummy.setAttribute(`readonly`, ``);
    dummy.value = text;
    return dummy;
  }
}

registerMPElement(`mp-share-button`, ShareButton);
